import React, {useMemo, useState} from "react";
import PropTypes from "prop-types";
import Header from "../components/Header";
import Sidebar from "../components/Sidebar";
import {useAuth} from "../authentication/AuthContext";
import {useLocation, useNavigate, useParams} from "react-router-dom";
import {useCreatePracticeSet, useDeletePracticeSet, usePracticeSet, useUpdatePracticeSet} from "./hooks/usePractice";
import {useNoteById} from "../note/hooks/useNote";
import {useProject} from "../project/hooks/useProjects";
import AddPracticeSetModal from "./components/AddPracticeSetModal";
import PracticeContent from "./components/PracticeContent";
import {ChevronRight, Code2, CheckCircle2, CircleSlash, Eye, Layers3, Play, Save, Send, Trash2, X} from "lucide-react";
import Editor from "react-simple-code-editor";
import Prism from "prismjs";
import "prismjs/components/prism-clike";
import "prismjs/components/prism-javascript";
import "prismjs/components/prism-python";
import "prismjs/components/prism-java";
import "prismjs/components/prism-sql";
import "prismjs/components/prism-go";
import "prismjs/themes/prism-tomorrow.css";

function normalizeAnswer(value) {
    return String(value ?? "").replace(/\r\n/g, "\n").trim();
}

function normalizeCodeForValidation(value) {
    return normalizeAnswer(value).replace(/\s+/g, ' ');
}

function ChoiceItem({item}) {
    const [selectedOptionIds, setSelectedOptionIds] = useState([]);
    const [submitted, setSubmitted] = useState(false);
    const isMultiple = Boolean(item.allowMultipleAnswers);
    const correctOptionIds = (item.options ?? []).filter((option) => option.isCorrect).map((option) => option.id);
    const selectedSet = new Set(selectedOptionIds);
    const correctSet = new Set(correctOptionIds);
    const isCorrect =
        submitted
        && selectedOptionIds.length === correctOptionIds.length
        && selectedOptionIds.every((optionId) => correctSet.has(optionId));

    const handleRedo = () => {
        setSelectedOptionIds([]);
        setSubmitted(false);
    };

    return (
        <div className="space-y-3">
            <div className="text-xs uppercase tracking-wide text-gray-500">
                {isMultiple ? 'Select one or more answers' : 'Select one answer'}
            </div>
            <div className="space-y-2">
                {item.options?.map((option) => {
                    const selected = selectedSet.has(option.id);
                    const isOptionCorrect = option.isCorrect;
                    const optionClass = submitted
                        ? selected && isOptionCorrect
                            ? 'practice-answer-correct border-emerald-500/40 bg-emerald-500/10'
                            : selected && !isOptionCorrect
                                ? 'practice-answer-incorrect border-red-500/40 bg-red-500/10'
                                : isOptionCorrect
                                    ? 'practice-answer-correct border-emerald-500/30 bg-emerald-500/10'
                                    : 'border-[#31384C] bg-[#2A2B32] text-gray-300'
                        : selected
                            ? 'practice-answer-selected border-indigo-500 bg-indigo-500/15'
                            : 'border-[#31384C] bg-[#2A2B32] text-gray-300 hover:bg-[#30384D]';

                    return (
                        <button
                            key={option.id}
                            type="button"
                            onClick={() => {
                                if (submitted) return;

                                if (isMultiple) {
                                    setSelectedOptionIds((prev) => (
                                        prev.includes(option.id)
                                            ? prev.filter((id) => id !== option.id)
                                            : [...prev, option.id]
                                    ));
                                    return;
                                }

                                setSelectedOptionIds([option.id]);
                            }}
                            className={`flex w-full items-center gap-2 rounded-lg border px-3 py-2 text-left text-sm transition ${optionClass}`}
                        >
                            {isMultiple ? (
                                <span className={`flex h-4 w-4 items-center justify-center rounded border ${
                                    submitted && selected && isOptionCorrect ? 'border-emerald-400 bg-emerald-500' :
                                    submitted && selected && !isOptionCorrect ? 'border-red-400 bg-red-500' :
                                    selected ? 'border-indigo-400 bg-indigo-500' : 'border-gray-500'
                                }`}>
                                    {selected && <CheckCircle2 size={10} />}
                                </span>
                            ) : (
                                <span className={`h-4 w-4 rounded-full border ${
                                    submitted && selected && isOptionCorrect ? 'border-emerald-400 bg-emerald-500' :
                                    submitted && selected && !isOptionCorrect ? 'border-red-400 bg-red-500' :
                                    selected ? 'border-indigo-400 bg-indigo-500' : 'border-gray-500'
                                }`} />
                            )}
                            <span className="flex-1">{option.text}</span>
                            {submitted && isOptionCorrect && <CheckCircle2 size={16} className="text-emerald-400" />}
                            {submitted && selected && !isOptionCorrect && <CircleSlash size={16} className="text-red-400" />}
                        </button>
                    );
                })}
            </div>

            <div className="flex items-center gap-3">
                <button
                    type="button"
                    onClick={() => setSubmitted(true)}
                    disabled={selectedOptionIds.length === 0}
                    className="inline-flex items-center gap-2 rounded-lg bg-indigo-600 px-4 py-2 text-sm font-medium text-white transition hover:bg-indigo-500 disabled:cursor-not-allowed disabled:opacity-60"
                >
                    <Send size={15} />
                    Submit
                </button>

                {submitted && (
                    <button
                        type="button"
                        onClick={handleRedo}
                        className="inline-flex items-center gap-2 rounded-lg border border-[#3A4257] px-4 py-2 text-sm font-medium text-gray-200 transition hover:bg-[#2B3242]"
                    >
                        Redo
                    </button>
                )}

                {submitted && (
                    <span className={`text-sm font-medium ${isCorrect ? 'text-emerald-400' : 'text-amber-400'}`}>
                        {isCorrect ? 'Correct' : 'Not quite'}
                    </span>
                )}
            </div>

            {submitted && (
                <div className="space-y-2">
                    <p className="text-xs uppercase tracking-wide text-gray-500">Correct answer</p>
                    <div className="space-y-2">
                        {item.options?.filter((option) => option.isCorrect).map((option) => (
                            <div key={option.id} className="practice-answer-correct flex items-center gap-2 rounded-lg border border-emerald-500/20 bg-emerald-500/10 px-3 py-2 text-sm">
                                <CheckCircle2 size={16} className="text-emerald-400" />
                                <span>{option.text}</span>
                            </div>
                        ))}
                    </div>
                </div>
            )}
        </div>
    );
}

ChoiceItem.propTypes = {
    item: PropTypes.shape({
        options: PropTypes.arrayOf(PropTypes.shape({
            id: PropTypes.string,
            text: PropTypes.string,
            isCorrect: PropTypes.bool,
        })),
    }).isRequired,
};

function GuidedCodingItem({item}) {
    const steps = item.guidedSteps ?? [];
    const [currentStepIndex, setCurrentStepIndex] = useState(0);
    const [code, setCode] = useState('');
    const [checkResult, setCheckResult] = useState(null);
    const language = item.language || 'go';
    const currentStep = steps[currentStepIndex];
    const highlight = useMemo(
        () => (value) => Prism.highlight(value, Prism.languages[language] ?? Prism.languages.go, language),
        [language]
    );

    if (!currentStep) {
        return <div className="mt-4 rounded-xl border border-amber-500/20 bg-amber-500/10 p-4 text-sm text-amber-100">No guided steps have been added yet.</div>;
    }

    const checkStep = () => {
        const expected = normalizeCodeForValidation(currentStep.validation);
        const attempt = normalizeCodeForValidation(code);
        setCheckResult(Boolean(attempt) && (!expected || attempt.includes(expected)));
    };
    const isLastStep = currentStepIndex === steps.length - 1;

    return (
        <div className="mt-5 space-y-4">
            <div className="flex flex-wrap items-center justify-between gap-3">
                <div>
                    <p className="text-sm font-semibold text-gray-100">Step {currentStepIndex + 1} of {steps.length}</p>
                    <p className="mt-2 whitespace-pre-wrap text-gray-200">{currentStep.instruction}</p>
                </div>
                <span className="rounded-lg border border-[#31384C] bg-[#2A2B32] px-3 py-1.5 text-xs font-medium uppercase text-gray-300">{language}</span>
            </div>

            <div className="overflow-hidden rounded-xl border border-[#31384C] bg-[#0f1117]">
                <div className="flex items-center gap-2 border-b border-[#31384C] px-3 py-2 text-xs text-gray-400">
                    <Code2 size={14} className="text-indigo-400" /> Code editor
                </div>
                <Editor
                    value={code}
                    onValueChange={(value) => { setCode(value); setCheckResult(null); }}
                    highlight={highlight}
                    padding={16}
                    spellCheck={false}
                    style={{fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace", fontSize: 13, background: "#0f1117", minHeight: "220px", lineHeight: "1.6", caretColor: "white"}}
                />
            </div>

            <div className="flex flex-wrap items-center justify-between gap-3">
                <div className="flex items-center gap-3">
                    <button type="button" onClick={checkStep} disabled={!normalizeAnswer(code)} className="inline-flex items-center gap-2 rounded-lg bg-indigo-600 px-5 py-2 text-sm font-medium text-white hover:bg-indigo-500 disabled:cursor-not-allowed disabled:opacity-60">
                        <CheckCircle2 size={15} /> Check
                    </button>
                    {checkResult !== null && (
                        <span className={`text-sm font-medium ${checkResult ? 'text-emerald-400' : 'text-amber-400'}`}>
                            {checkResult ? 'Step complete' : 'The expected code is not present yet'}
                        </span>
                    )}
                </div>
                <button
                    type="button"
                    disabled={!checkResult || isLastStep}
                    onClick={() => { setCurrentStepIndex((index) => index + 1); setCheckResult(null); }}
                    className="inline-flex items-center gap-1 rounded-lg border border-[#3A4257] px-4 py-2 text-sm font-medium text-gray-100 hover:bg-[#2B3242] disabled:cursor-not-allowed disabled:opacity-40"
                >
                    {isLastStep && checkResult ? 'Completed' : 'Next Step'} <ChevronRight size={16} />
                </button>
            </div>
        </div>
    );
}

GuidedCodingItem.propTypes = {
    item: PropTypes.shape({
        language: PropTypes.string,
        guidedSteps: PropTypes.arrayOf(PropTypes.shape({instruction: PropTypes.string, validation: PropTypes.string})),
    }).isRequired,
};

function PracticeItemCard({item}) {
    const [attempt, setAttempt] = useState("");
    const [submitted, setSubmitted] = useState(false);
    const [showExplanation, setShowExplanation] = useState(false);
    const expectedAnswer = item.expectedAnswer ?? item.correctAnswer ?? "";
    const isCodeQuestion = item.questionType === 'CODE_INPUT' || item.questionType === 'CODE_SYNTAX';
    const isOpenEndedQuestion = item.questionType === 'OPEN_ENDED';
    const language = item.language || 'go';
    const highlight = useMemo(() => {
        return (code) => Prism.highlight(code, Prism.languages[language] ?? Prism.languages.go, language);
    }, [language]);
    const isCorrect = submitted && normalizeAnswer(attempt) === normalizeAnswer(expectedAnswer);
    const [trueFalseSelection, setTrueFalseSelection] = useState(null);
    const [trueFalseSubmitted, setTrueFalseSubmitted] = useState(false);
    const trueFalseIsCorrect = trueFalseSubmitted && trueFalseSelection === item.correctAnswer;
    const isInteractiveTrueFalse = item.questionType === 'TRUE_FALSE';
    const redoCode = () => {
        setAttempt("");
        setSubmitted(false);
    };
    const redoTrueFalse = () => {
        setTrueFalseSelection(null);
        setTrueFalseSubmitted(false);
    };

    return (
        <div className="rounded-2xl border border-[#2A2B32] bg-[#1E1F25] p-5">
            {!isOpenEndedQuestion && (
                <div className="mb-3 flex items-center gap-2 text-sm font-semibold text-gray-100">
                    <Layers3 size={16} className="text-indigo-400" />
                    {item.questionType?.replace('_', ' ') ?? 'QUESTION'}
                </div>
            )}
            <PracticeContent text={item.prompt} language={language} textClassName="text-gray-200" />

            {item.questionType === 'MULTIPLE_CHOICE' && (
                <div className="mt-4">
                    <ChoiceItem item={item} />
                </div>
            )}

            {isInteractiveTrueFalse && (
                <div className="mt-4 space-y-3">
                    <div className="text-xs uppercase tracking-wide text-gray-500">Select one answer</div>
                    <div className="flex gap-2">
                        <button
                            type="button"
                            onClick={() => {
                                if (!trueFalseSubmitted) setTrueFalseSelection(true);
                            }}
                            className={`flex-1 rounded-lg border px-3 py-2 text-sm font-medium transition ${
                                trueFalseSubmitted
                                    ? (item.correctAnswer === true
                                        ? 'border-emerald-500 bg-emerald-500/10 text-emerald-100'
                                        : trueFalseSelection === true
                                            ? 'border-red-500 bg-red-500/10 text-red-100'
                                            : 'border-[#31384C] bg-[#2A2B32] text-gray-300')
                                    : trueFalseSelection === true
                                        ? 'border-indigo-500 bg-indigo-500/15 text-indigo-100'
                                        : 'border-[#31384C] bg-[#2A2B32] text-gray-300 hover:bg-[#30384D]'
                            }`}
                        >
                            True
                        </button>
                        <button
                            type="button"
                            onClick={() => {
                                if (!trueFalseSubmitted) setTrueFalseSelection(false);
                            }}
                            className={`flex-1 rounded-lg border px-3 py-2 text-sm font-medium transition ${
                                trueFalseSubmitted
                                    ? (item.correctAnswer === false
                                        ? 'border-emerald-500 bg-emerald-500/10 text-emerald-100'
                                        : trueFalseSelection === false
                                            ? 'border-red-500 bg-red-500/10 text-red-100'
                                            : 'border-[#31384C] bg-[#2A2B32] text-gray-300')
                                    : trueFalseSelection === false
                                        ? 'border-indigo-500 bg-indigo-500/15 text-indigo-100'
                                        : 'border-[#31384C] bg-[#2A2B32] text-gray-300 hover:bg-[#30384D]'
                            }`}
                        >
                            False
                        </button>
                    </div>

                    <div className="flex items-center gap-3">
                        <button
                            type="button"
                            onClick={() => setTrueFalseSubmitted(true)}
                            disabled={trueFalseSelection === null}
                            className="inline-flex items-center gap-2 rounded-lg bg-indigo-600 px-4 py-2 text-sm font-medium text-white transition hover:bg-indigo-500 disabled:cursor-not-allowed disabled:opacity-60"
                        >
                            <Send size={15} />
                            Submit
                        </button>

                        {trueFalseSubmitted && (
                            <button
                                type="button"
                                onClick={redoTrueFalse}
                                className="inline-flex items-center gap-2 rounded-lg border border-[#3A4257] px-4 py-2 text-sm font-medium text-gray-200 transition hover:bg-[#2B3242]"
                            >
                                Redo
                            </button>
                        )}

                        {trueFalseSubmitted && (
                            <span className={`text-sm font-medium ${trueFalseIsCorrect ? 'text-emerald-400' : 'text-amber-400'}`}>
                                {trueFalseIsCorrect ? 'Correct' : 'Not quite'}
                            </span>
                        )}
                    </div>

                    {trueFalseSubmitted && (
                        <div className="rounded-xl border border-[#31384C] bg-[#0f1117] px-3 py-2 text-sm text-gray-200">
                            Correct answer: {item.correctAnswer === true ? 'True' : 'False'}
                        </div>
                    )}
                </div>
            )}

            {isCodeQuestion && (
                <div className="mt-4 space-y-4">
                    <div className="inline-flex items-center gap-2 rounded-lg border border-[#31384C] bg-[#2A2B32] px-3 py-2 text-sm text-gray-200">
                        <Code2 size={16} className="text-indigo-400" />
                        Language: {language || 'unspecified'}
                    </div>

                    <div className="overflow-hidden rounded-xl border border-[#31384C] bg-[#0f1117]">
                        <div className="flex items-center gap-2 border-b border-[#31384C] px-3 py-2 text-xs text-gray-400">
                            <Play size={14} className="text-indigo-400" />
                            Write your answer
                        </div>
                        <Editor
                            value={attempt}
                            onValueChange={setAttempt}
                            highlight={highlight}
                            padding={14}
                            spellCheck={false}
                            style={{
                                fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace",
                                fontSize: 13,
                                background: "#0f1117",
                                minHeight: "130px",
                                lineHeight: "1.6",
                                caretColor: "white",
                            }}
                        />
                    </div>

                    <div className="flex items-center gap-3">
                        <button
                            type="button"
                            onClick={() => setSubmitted(true)}
                            className="inline-flex items-center gap-2 rounded-lg bg-indigo-600 px-4 py-2 text-sm font-medium text-white transition hover:bg-indigo-500"
                        >
                            <Send size={15} />
                            Submit
                        </button>

                        {submitted && (
                            <button
                                type="button"
                                onClick={redoCode}
                                className="inline-flex items-center gap-2 rounded-lg border border-[#3A4257] px-4 py-2 text-sm font-medium text-gray-200 transition hover:bg-[#2B3242]"
                            >
                                Redo
                            </button>
                        )}

                        {submitted && (
                            <span className={`text-sm font-medium ${isCorrect ? 'text-emerald-400' : 'text-amber-400'}`}>
                                {isCorrect ? 'Correct' : 'Not quite'}
                            </span>
                        )}
                    </div>

                    {submitted && (
                        <div className="space-y-2">
                            <p className="text-xs uppercase tracking-wide text-gray-500">Correct answer</p>
                            <PracticeContent text={expectedAnswer} language={language} textClassName="rounded-xl border border-[#31384C] bg-[#0f1117] p-4 text-sm text-gray-100" />
                        </div>
                    )}
                </div>
            )}

            {isOpenEndedQuestion && (
                <div className="mt-4 space-y-4">
                    <div className="overflow-hidden rounded-xl border border-[#31384C] bg-[#0f1117]">
                        <div className="border-b border-[#31384C] px-3 py-2 text-xs text-gray-400">
                            Write your answer
                        </div>
                        <textarea
                            value={attempt}
                            onChange={(event) => setAttempt(event.target.value)}
                            disabled={submitted}
                            rows={5}
                            className="w-full resize-y bg-[#0f1117] p-4 text-sm leading-6 text-gray-100 outline-none placeholder:text-gray-600 disabled:cursor-not-allowed disabled:opacity-80"
                            placeholder="Type your answer here"
                        />
                    </div>

                    <div className="flex flex-wrap items-center gap-3">
                        <button
                            type="button"
                            onClick={() => setSubmitted(true)}
                            disabled={!normalizeAnswer(attempt)}
                            className="inline-flex items-center gap-2 rounded-lg bg-indigo-600 px-4 py-2 text-sm font-medium text-white transition hover:bg-indigo-500 disabled:cursor-not-allowed disabled:opacity-60"
                        >
                            <Send size={15} />
                            Submit
                        </button>

                        {submitted && (
                            <button
                                type="button"
                                onClick={redoCode}
                                className="inline-flex items-center gap-2 rounded-lg border border-[#3A4257] px-4 py-2 text-sm font-medium text-gray-200 transition hover:bg-[#2B3242]"
                            >
                                Redo
                            </button>
                        )}

                        {submitted && (
                            <span className={`text-sm font-medium ${isCorrect ? 'text-emerald-400' : 'text-amber-400'}`}>
                                {isCorrect ? 'Correct' : 'Not quite'}
                            </span>
                        )}
                    </div>

                    {submitted && (
                        <div className="space-y-2">
                            <p className="text-xs uppercase tracking-wide text-gray-500">Expected answer</p>
                            <PracticeContent text={expectedAnswer} language={language} textClassName="rounded-xl border border-[#31384C] bg-[#0f1117] p-4 text-sm text-gray-100" />
                        </div>
                    )}
                </div>
            )}

            {item.questionType === 'GUIDED_CODING' && <GuidedCodingItem item={item} />}

            {item.explanation && (
                <div className="mt-4">
                    <button
                        type="button"
                        onClick={() => setShowExplanation((current) => !current)}
                        className="inline-flex items-center gap-2 text-sm font-medium text-indigo-300 transition hover:text-indigo-200"
                    >
                        <Eye size={15} />
                        {showExplanation ? 'Hide explanation' : 'Show explanation'}
                    </button>
                    {showExplanation && (
                        <div className="mt-2">
                            <PracticeContent text={item.explanation} language={language} textClassName="rounded-xl border border-[#31384C] bg-[#2A2B32] p-3 text-sm text-gray-300" />
                        </div>
                    )}
                </div>
            )}
        </div>
    );
}

PracticeItemCard.propTypes = {
    item: PropTypes.shape({
        id: PropTypes.string,
        questionType: PropTypes.string,
        prompt: PropTypes.string,
        correctAnswer: PropTypes.oneOfType([PropTypes.bool, PropTypes.string]),
        language: PropTypes.string,
        expectedAnswer: PropTypes.string,
        explanation: PropTypes.string,
        options: PropTypes.array,
        guidedSteps: PropTypes.array,
    }).isRequired,
};

export default function PracticeDetailsPage() {
    const {id} = useParams();
    const {authToken, user} = useAuth();
    const navigate = useNavigate();
    const location = useLocation();
    const isPreview = id === 'preview';
    const previewPracticeSet = isPreview ? location.state?.generatedQuiz ?? null : null;
    const {data: storedPracticeSet, isLoading, error} = usePracticeSet(authToken, isPreview ? null : id);
    const practiceSet = isPreview ? previewPracticeSet : storedPracticeSet;
    const {data: sourceNote} = useNoteById(authToken, practiceSet?.noteId);
    const {data: project, isLoading: isProjectLoading} = useProject(authToken, practiceSet?.projectId);
    const noteTitle = location.state?.noteTitle || sourceNote?.title || '';
    const {mutateAsync: createPracticeSet, isLoading: isSaving} = useCreatePracticeSet(authToken);
    const {mutate: updatePracticeSet, isLoading: isUpdating} = useUpdatePracticeSet(authToken);
    const {mutateAsync: deletePracticeSet} = useDeletePracticeSet(authToken);
    const [showEditModal, setShowEditModal] = useState(false);
    const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
    const [isDeleting, setIsDeleting] = useState(false);
    const [saveError, setSaveError] = useState('');

    const handleUpdate = (practiceSetData) => {
        updatePracticeSet(
            {
                practiceSetId: practiceSet.id,
                practiceSetData: {
                    ...practiceSetData,
                    user_id: practiceSet.userId ?? practiceSet.user_id ?? user?.uid ?? null,
                },
            },
            {onSuccess: () => setShowEditModal(false)}
        );
    };

    const handleDelete = async () => {
        if (!practiceSet?.id || isDeleting) return;

        setIsDeleting(true);
        setSaveError('');
        try {
            await deletePracticeSet(practiceSet.id);
            navigate('/practice');
        } catch {
            setSaveError('Practice set could not be deleted. Please try again.');
            setShowDeleteConfirm(false);
        } finally {
            setIsDeleting(false);
        }
    };

    const handleSavePreview = async () => {
        if (!practiceSet || isSaving) return;
        setSaveError('');
        try {
            const savedPracticeSet = await createPracticeSet({
                ...practiceSet,
                user_id: practiceSet.userId ?? practiceSet.user_id ?? user?.uid ?? null,
            });
            navigate(`/practice/${savedPracticeSet.id}`, {replace: true});
        } catch (savePreviewError) {
            setSaveError(savePreviewError instanceof Error ? savePreviewError.message : 'Quiz could not be saved.');
        }
    };

    return (
        <div className="flex min-h-screen flex-col bg-[var(--bg)]">
            <Header />
            <div className="flex flex-1 gap-3 bg-[#1C1F26] px-3 pb-4 pt-[76px] md:gap-4 md:px-4">
                <Sidebar />
                <div className="min-w-0 flex-1 rounded-[10px] bg-primary">
                    <div className="mx-auto w-full max-w-5xl px-4 py-8 md:px-6 md:py-10">
                        {isLoading && (
                            <div className="space-y-4">
                                <div className="h-10 w-64 animate-pulse rounded-lg bg-[#2A2B32]" />
                                <div className="h-40 animate-pulse rounded-2xl bg-[#1E1F25]" />
                            </div>
                        )}

                        {error && (
                            <div className="rounded-2xl border border-red-500/20 bg-red-500/10 p-5 text-red-200">
                                Failed to load practice set.
                            </div>
                        )}

                        {isPreview && !practiceSet && (
                            <div className="rounded-2xl border border-amber-500/20 bg-amber-500/10 p-5 text-amber-100">
                                This unsaved quiz is no longer available. Generate it again from the note.
                            </div>
                        )}

                        {!isLoading && practiceSet && (
                            <div className="space-y-6">
                                <div className="flex items-start justify-between gap-4">
                                    <div>
                                        <h1 className="text-2xl font-bold text-gray-100">{practiceSet.title || 'Untitled practice set'}</h1>
                                        {practiceSet.description && (
                                            <p className="mt-2 max-w-3xl text-sm text-gray-500">{practiceSet.description}</p>
                                        )}
                                    </div>

                                    <div className="flex gap-2">
                                        {isPreview ? (
                                            <>
                                                <button
                                                    type="button"
                                                    onClick={() => navigate(`/note/${practiceSet.noteId}`)}
                                                    disabled={isSaving}
                                                    className="inline-flex items-center gap-2 rounded-xl border border-[#3A4257] bg-[#2A2B32] px-4 py-2 text-sm font-medium text-gray-200 transition hover:bg-[#30384D] disabled:opacity-60"
                                                >
                                                    <X size={16} />
                                                    Discard
                                                </button>
                                                <button
                                                    type="button"
                                                    onClick={handleSavePreview}
                                                    disabled={isSaving}
                                                    className="inline-flex items-center gap-2 rounded-xl bg-indigo-600 px-4 py-2 text-sm font-medium text-white transition hover:bg-indigo-500 disabled:cursor-not-allowed disabled:opacity-60"
                                                >
                                                    <Save size={16} />
                                                    {isSaving ? 'Saving...' : 'Save quiz'}
                                                </button>
                                            </>
                                        ) : (
                                            <>
                                                <button
                                                    type="button"
                                                    onClick={() => setShowEditModal(true)}
                                                    className="rounded-xl border border-[#3A4257] bg-[#2A2B32] px-4 py-2 text-sm font-medium text-gray-200 transition hover:bg-[#30384D]"
                                                >
                                                    Edit
                                                </button>
                                                <button
                                                    type="button"
                                                    onClick={() => setShowDeleteConfirm(true)}
                                                    disabled={isDeleting}
                                                    className="inline-flex items-center gap-2 rounded-xl border border-red-500/30 bg-red-500/10 px-4 py-2 text-sm font-medium text-red-200 transition hover:bg-red-500/20 disabled:cursor-not-allowed disabled:opacity-60"
                                                >
                                                    <Trash2 size={16} />
                                                    {isDeleting ? 'Deleting...' : 'Delete'}
                                                </button>
                                            </>
                                        )}
                                    </div>
                                </div>

                                {saveError && (
                                    <div className="rounded-xl border border-red-500/20 bg-red-500/10 px-4 py-3 text-sm text-red-200">
                                        {saveError}
                                    </div>
                                )}

                                <div className="flex flex-wrap gap-2 text-xs">
                                    <span className="rounded-full border border-indigo-500/20 bg-indigo-500/10 px-2.5 py-1 font-medium text-indigo-300">
                                        {practiceSet.practiceItems?.length ?? 0} question(s)
                                    </span>
                                    {practiceSet.projectId && (
                                        <span className="rounded-full border border-[#31384C] bg-[#2A2B32] px-2.5 py-1 text-gray-300">
                                            Project: {isProjectLoading ? 'Loading...' : (project?.name || 'Unknown project')}
                                        </span>
                                    )}
                                    {practiceSet.noteId && (
                                        <span className="rounded-full border border-[#31384C] bg-[#2A2B32] px-2.5 py-1 text-gray-300">
                                            Note: {noteTitle || 'Loading note...'}
                                        </span>
                                    )}
                                </div>

                                <div className="space-y-4">
                                    {(practiceSet.practiceItems ?? []).map((item, index) => (
                                        <PracticeItemCard key={item.id ?? `${item.questionType}-${index}`} item={item} />
                                    ))}
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </div>

            {showEditModal && practiceSet && !isPreview && (
                <AddPracticeSetModal
                    initialPracticeSet={practiceSet}
                    isSubmitting={isUpdating}
                    onClose={() => setShowEditModal(false)}
                    onSubmit={handleUpdate}
                />
            )}

            {showDeleteConfirm && practiceSet && !isPreview && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 px-4 backdrop-blur-sm">
                    <div className="w-full max-w-md rounded-2xl border border-[#2A2B32] bg-[#1E1F25] p-6 shadow-xl">
                        <h2 className="text-xl font-semibold text-gray-100">Delete practice set?</h2>
                        <p className="mt-2 text-sm text-gray-400">
                            Are you sure you want to delete <span className="font-medium text-gray-200">{practiceSet.title || 'this practice set'}</span>? This cannot be undone.
                        </p>

                        <div className="mt-6 flex justify-end gap-3">
                            <button
                                type="button"
                                onClick={() => setShowDeleteConfirm(false)}
                                className="rounded-lg border border-[#3A4257] px-4 py-2 text-sm font-medium text-gray-200 transition hover:bg-[#2B3242]"
                            >
                                Cancel
                            </button>
                            <button
                                type="button"
                                onClick={handleDelete}
                                disabled={isDeleting}
                                className="rounded-lg bg-red-600 px-4 py-2 text-sm font-medium text-white transition hover:bg-red-500 disabled:cursor-not-allowed disabled:opacity-60"
                            >
                                {isDeleting ? 'Deleting...' : 'Delete'}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
